import { paths } from "./userinput/paths";

// The output of this system is activeTip. There are named tips (eg locomotion) that each have validators.
//
// Each frame we run all the non-finished validators and take the first tip (by order)
// which is VALID. Any tip that returns FINISH will no longer be considered without
// a local storage reset.

// Validators can return these values:
//
// INVALID - Tip is not valid to show
// VALID - Tip is valid to show
// FINISH - Tip should always be assumed INVALID going forward
//
const INVALID = 0;
const VALID = 1;
const FINISH = 2;

const LOCAL_STORAGE_KEY = "__finished_tips";
const COLLABORATOR_LOCAL_STORAGE_KEY = "__finished_collaborator_tips";
const TIPS_VERSION_KEY = "__tips_version";
const CURRENT_TIPS_VERSION = "1.0"; // Increment this when making breaking changes to the tip system

// Mapping of tips to their required permissions and config settings
const TIP_REQUIREMENTS = {
  fly: { permission: "fly", config: "shortcuts.enabled" },
  upload: { permission: "spawn_and_move_media", config: "spawnAndMoveMedia.enabled" },
  import: { permission: "spawn_and_move_media", config: "spawnAndMoveMedia.enabled" },
  pin: { permission: "pin_objects", config: "spawnAndMoveMedia.enabled" },
  teleport: { permission: "spawn_and_move_media", config: "spawnAndMoveMedia.enabled" },
  interact: {
    permission: "spawn_and_move_media",
    configs: ["spawnAndMoveMedia.enabled", "VRUI.enabled"]
  },
  locomotion: { config: "joystick.enabled", mobileOnly: true },
};

const TIPS = {
  desktop: ["intro", "locomotion", "locomotionBoost", "turning", "teleport", "interact", "fly", "end"],
  mobile: ["intro", "locomotion", "look", "interact", "end"],
  standalone: []
};

const COLLABORATOR_TIPS = {
  desktop: ["cIntro", "upload", "import", "pin"],
  mobile: ["cIntro", "upload", "import", "pin"],
  standalone: []
};

let localStorageCache = null;
let finished = false; // Optimization, lets system skip altogether once finished.
let teleportPressStart = null;
let teleportPressed = false;

const isMobile = AFRAME.utils.device.isMobile();
const isMobileVR = AFRAME.utils.device.isMobileVR();

const tipPlatform = () => {
  if (isMobileVR) return "standalone";
  return isMobile ? "mobile" : "desktop";
};

// Helper function to check if a tip should be shown based on permissions and config
function canShowTip(tip) {
  const requirements = TIP_REQUIREMENTS[tip];
  if (!requirements) return true; // No requirements

  // Check permission if required
  if (requirements.permission) {
    if (!window.APP.hubChannel || !window.APP.hubChannel.can(requirements.permission)) {
      return false;
    }
  }

  // Check single config if required
  if (requirements.config) {
    // If mobileOnly is true, only check config on mobile devices
    if (requirements.mobileOnly && !isMobile) {
      return true; // Skip config check on desktop
    }

    const configPath = requirements.config.split(".");
    let configValue = window.ConfigManager?.config;

    for (const path of configPath) {
      configValue = configValue?.[path];
      if (configValue === undefined) {
        return false;
      }
    }

    if (!configValue) {
      return false;
    }
  }

  // Check multiple configs if required
  if (requirements.configs) {
    for (const configPath of requirements.configs) {
      const paths = configPath.split(".");
      let configValue = window.ConfigManager?.config;

      for (const path of paths) {
        configValue = configValue?.[path];
        if (configValue === undefined) {
          return false;
        }
      }

      if (!configValue) {
        return false;
      }
    }
  }

  return true;
}

function getTipsForSet(set) {
  const baseTips = set === "collaborator" ? COLLABORATOR_TIPS[tipPlatform()] : TIPS[tipPlatform()];

  // Filter tips based on permissions and config
  const availableTips = baseTips.filter(tip => canShowTip(tip));

  // If this is intro/cIntro and no other steps are available, don't show it
  if (set === "main" && availableTips.length <= 2) {
    // Only intro and end
    return availableTips.filter(tip => tip !== "intro");
  }

  if (set === "collaborator" && availableTips.length <= 1) {
    // Only cIntro
    return availableTips.filter(tip => tip !== "cIntro");
  }

  return availableTips;
}

function getLocalStorageKeyForSet(set) {
  return set === "collaborator" ? COLLABORATOR_LOCAL_STORAGE_KEY : LOCAL_STORAGE_KEY;
}

function markTipFinished(tip, set = "main") {
  const key = getLocalStorageKeyForSet(set);
  const storeData = JSON.parse(localStorage.getItem(key));
  storeData[tip] = { finished: true };
  localStorage.setItem(key, JSON.stringify(storeData));
  localStorageCache = null;
}

function storedStateForTip(tip, set = "main") {
  const key = getLocalStorageKeyForSet(set);
  const storeData = JSON.parse(localStorage.getItem(key));
  return storeData[tip] && storeData[tip]["finished"] === true ? FINISH : VALID;
}

const VALIDATORS = {
  intro: function () {
    return VALID;
  },
  end: function () {
    return storedStateForTip("end");
  },
  look: function (userinput, scene) {
    const cameraDelta = userinput.get(
      isMobile ? paths.device.touchscreen.touchCameraDelta : paths.device.mouse.buttonLeft
    );
    // Only finish if this tip is currently active
    if (cameraDelta && scene.systems.tips?.activeTip === `tips.${tipPlatform()}.look`) {
      return FINISH;
    }
    return VALID;
  },
  interact: function (userinput) {
    return userinput.get(paths.actions.cursor.right.grab) ? FINISH : VALID;
  },
  locomotion: function (userinput) {
    const accel = userinput.get(paths.actions.characterAcceleration);

    if (isMobile) {
      const finishLocomotion = () => {
        markTipFinished("locomotion");
      };

      const interval = setInterval(() => {
        const nipples = document.querySelectorAll(".nipple");

        if (nipples.length > 0) {
          clearInterval(interval);

          nipples.forEach(nipple => {
            nipple.addEventListener("touchend", finishLocomotion, { once: true });
          });
        }
      }, 1000);
    }

    return accel && (accel[0] !== 0 || accel[1] !== 0) ? FINISH : VALID;
  },
  locomotionBoost: function (userinput) {
    return userinput.get(paths.actions.boost) ? FINISH : VALID;
  },
  teleport: function (userinput) {
    const start = userinput.get(paths.actions.startGazeTeleport);
    const stop = userinput.get(paths.actions.stopGazeTeleport);
    if (start) {
      teleportPressed = true;
      if (!teleportPressStart) teleportPressStart = performance.now();
    }
    if (teleportPressed) {
      if (performance.now() - teleportPressStart >= 750) {
        teleportPressStart = null;
        teleportPressed = false;
        return FINISH;
      }
    }
    if (stop) {
      teleportPressStart = null;
      teleportPressed = false;
    }
    return VALID;
  },
  turning: function (userinput) {
    if (
      userinput.get(paths.actions.snapRotateLeft) ||
      userinput.get(paths.actions.snapRotateRight) ||
      userinput.get(isMobile ? paths.device.touchscreen.touchCameraDelta : paths.device.smartMouse.cameraDelta)
    ) {
      return FINISH;
    }
    return VALID;
  },
  invite: function (_userinput, scene, hub) {
    if (hub && hub.entry_mode === "invite") return INVALID;
    return scene.is("copresent") ? FINISH : VALID;
  },
  menu: function () {
    return storedStateForTip("menu");
  },
  fly: function (userinput) {
    if (userinput.get(paths.actions.toggleFly)) return FINISH;
    return VALID;
  },
  cIntro: function () {
    return storedStateForTip("cIntro", "upload");
  },
  upload: function () {
    return storedStateForTip("upload");
  },
  import: function () {
    return storedStateForTip("import");
  },
  pin: function () {
    return storedStateForTip("pin", "collaborator");
  }
};

AFRAME.registerSystem("tips", {
  init: function () {
    this.activeTip = null;
    this.activeTipSet = "main";
    this._performStep = this._performStep.bind(this);
    this.lastHubId = null; // Track the last hub to detect room changes

    // Check if we need to reset tips due to version change
    const storedVersion = localStorage.getItem(TIPS_VERSION_KEY);
    if (storedVersion !== CURRENT_TIPS_VERSION) {
      // Version changed, reset all tips
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({}));
      localStorage.setItem(COLLABORATOR_LOCAL_STORAGE_KEY, JSON.stringify({}));
      localStorage.setItem(TIPS_VERSION_KEY, CURRENT_TIPS_VERSION);
    } else {
      // Initialize both tip sets in localStorage if needed
      [LOCAL_STORAGE_KEY, COLLABORATOR_LOCAL_STORAGE_KEY].forEach(key => {
        const storeData = JSON.parse(localStorage.getItem(key));
        if (storeData === null) {
          localStorage.setItem(key, JSON.stringify({}));
        }
        // Note: We no longer clear tips based on filtering to preserve completion status
        // across different rooms with different permissions
      });
    }
  },

  resetTips: function () {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({}));
    localStorage.setItem(COLLABORATOR_LOCAL_STORAGE_KEY, JSON.stringify({}));
    localStorage.setItem(TIPS_VERSION_KEY, CURRENT_TIPS_VERSION);
    localStorageCache = null;
    window.APP.store.resetTipActivityFlags();
    window.APP.store.resetConfirmedBroadcastedRooms();
    this.activeTip = null;
    finished = false;
    this.activeTipSet = "main";
  },

  // Check if there are any newly available tips that weren't available before
  checkForNewlyAvailableTips: function () {
    const currentHubId = window.APP.hub?.hub_id;

    // If this is a new room, reset the finished flag to check for new tips
    if (this.lastHubId !== currentHubId) {
      this.lastHubId = currentHubId;
      finished = false;
      localStorageCache = null;

      // Check if there are any available tips that haven't been completed
      const mainTips = getTipsForSet("main");
      const collaboratorTips = getTipsForSet("collaborator");

      const mainStore = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "{}");
      const collaboratorStore = JSON.parse(localStorage.getItem(COLLABORATOR_LOCAL_STORAGE_KEY) || "{}");

      // Check if there are any available tips that haven't been completed
      const hasUnfinishedMainTips = mainTips.some(tip => !mainStore[tip] || !mainStore[tip].finished);
      const hasUnfinishedCollaboratorTips = collaboratorTips.some(
        tip => !collaboratorStore[tip] || !collaboratorStore[tip].finished
      );

      if (hasUnfinishedMainTips) {
        this.activeTipSet = "main";
        this.activeTip = null;
      } else if (hasUnfinishedCollaboratorTips) {
        this.activeTipSet = "collaborator";
        this.activeTip = null;
      }
    }
  },

  skipTip: function (selectedTipIndex) {
    const tips = getTipsForSet(this.activeTipSet);
    const tipId = tips[selectedTipIndex];
    markTipFinished(tipId, this.activeTipSet);
  },

  getTips: function () {
    return getTipsForSet(this.activeTipSet);
  },

  skipTips: function () {
    const tips = getTipsForSet(this.activeTipSet);
    for (let i = 0; i < tips.length; i++) {
      const tipId = tips[i];
      markTipFinished(tipId, this.activeTipSet);
    }
  },

  setActiveTip: function (tipId) {
    this.activeTip = tipId;
  },

  showCollaboratorTips: function () {
    this.activeTipSet = "collaborator";
    this.activeTip = null;
    finished = false;
    localStorage.setItem(COLLABORATOR_LOCAL_STORAGE_KEY, JSON.stringify({}));
  },

  showMainTips: function () {
    this.activeTipSet = "main";
    this.activeTip = null;
    finished = false;
  },

  tick: function () {
    if (isMobileVR) return; // Optimization for now, don't bother with this on mobile VR until we have real tips

    if (!this._userinput) {
      this._userinput = this.el.systems.userinput;

      if (!this._userinput) return;
    }

    // Check for newly available tips when entering a new room
    this.checkForNewlyAvailableTips();

    const tips = getTipsForSet(this.activeTipSet);

    const prevTip = this.activeTip;

    this._performStep(tips);

    if (prevTip !== this.activeTip) {
      this.el.emit("tip-changed", this.activeTip);
    }
  },

  _performStep: function (tips) {
    if (finished) return;

    let chosenTip = null;
    let finishCount = 0;

    for (let i = 0; i < tips.length; i++) {
      const tip = tips[i];

      if (localStorageCache === null) {
        const key = getLocalStorageKeyForSet(this.activeTipSet);
        localStorageCache = JSON.parse(localStorage.getItem(key));
      }

      if (localStorageCache[tip] && localStorageCache[tip].finished) {
        finishCount++;
        continue;
      }

      switch (VALIDATORS[tip](this._userinput, this.el, window.APP.hub)) {
        case FINISH:
          markTipFinished(tip, this.activeTipSet);
          break;
        case VALID:
          chosenTip = `tips.${tipPlatform()}.${tip}`;
          break;
      }

      if (chosenTip) break;
    }

    this.activeTip = chosenTip;

    if (finishCount === tips.length) {
      // Check if there are any available tips in the other set
      const otherSet = this.activeTipSet === "main" ? "collaborator" : "main";
      const otherTips = getTipsForSet(otherSet);
      const otherStore = JSON.parse(localStorage.getItem(getLocalStorageKeyForSet(otherSet)) || "{}");
      const hasUnfinishedOtherTips = otherTips.some(tip => !otherStore[tip] || !otherStore[tip].finished);

      if (hasUnfinishedOtherTips) {
        // Switch to the other tip set
        this.activeTipSet = otherSet;
        this.activeTip = null;
        finished = false;
      } else {
        // Only set finished to true if both sets are completed
        finished = true;
      }
    }
  }
});
